// customized textarea component - uncontrolled hybrid to avoid cursor position reset on re-render and still work with react-hook-form

import { DetailedHTMLProps, TextareaHTMLAttributes, useEffect, useRef, forwardRef } from 'react';
import { cn } from "src/lib/utils"

type TextareaProps = DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>;

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({ 
  value = '', 
  onChange, 
  className = '', 
  onBlur, 
  ...props 
}, forwardedRef) => {
  // Create an internal ref
  const internalRef = useRef<HTMLTextAreaElement>(null);
  
  // This function handles both refs - your internal one and the forwarded one
  const handleRefs = (element: HTMLTextAreaElement | null) => {
    // Update your internal ref
    if (internalRef) {
      (internalRef as any).current = element;
    }
    
    // Handle the forwarded ref
    if (typeof forwardedRef === 'function') {
      forwardedRef(element);
    } else if (forwardedRef) {
      forwardedRef.current = element;
    }
  };

  // Your existing effect to maintain cursor position
  useEffect(() => {    
    if (internalRef.current && internalRef.current.value !== value) {
      internalRef.current.value = value as string;
    }
  }, [value]);

  return (
    <textarea
      ref={handleRefs}
      className={cn(
        "flex min-h-[80px] w-full h-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      onChange={(e) => onChange?.(e)}
      onBlur={onBlur}
      {...props}
    />
  );
});

Textarea.displayName = "Textarea"

export { Textarea }